import { useContext } from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { NotificationContext } from './NotificationContext';
import { ReasonsType } from '../utils/Error/ErrorCode';

export type HTTP_STATUS =
  | 200
  | 201
  | 204
  | 400
  | 401
  | 403
  | 404
  | 413
  | 500
  | 501
  | 503
  | 504;
const SUCCESS_CODE = 2;
const CLIENT_ERROR_CODE = 4;
const SERVER_ERROR_CODE = 5;
const MAX_FILE_SIZE_UPLOAD_EXCEEDED = 413;
const NOT_FOUND = 404;
function evaluateHTTPCode(status: HTTP_STATUS) {
  return {
    isSuccess: Math.trunc(status / 100) === SUCCESS_CODE,
    isClientError: Math.trunc(status / 100) === CLIENT_ERROR_CODE,
    isServerError: Math.trunc(status / 100) === SERVER_ERROR_CODE,
    isMaxFileSizeUploadExceeded: status === MAX_FILE_SIZE_UPLOAD_EXCEEDED,
    isNotFoundError: status === NOT_FOUND
  };
}

export interface DefaultResponseData {
  type: string;
  msg?: string;
  reasons?: Array<ReasonsType>;
}

export interface LoadingMessagesProps {
  successMessage?: string;
  errorMessage?: string;
  loadingMessage?: string;
  errorIdHandler?: Record<string, (reasons?: Array<ReasonsType>) => void>;
}

function isDefaultResponseData(
  responseData: unknown
): responseData is DefaultResponseData {
  return (
    responseData !== null &&
    typeof responseData === 'object' &&
    'type' in responseData &&
    typeof responseData.type === 'string'
  );
}

/**
 * Mapping Layer between API and UI
 * */
export function useNotification() {
  const { confirmByUser, setLoadingIndicators, notifyUser } =
    useContext(NotificationContext);
  const { t } = useTranslation();

  /**
   *  This functions maps from React-Query + Axios to the notification system. BE responses will be mapped from response -> (statusCode, message).
   * @param requestFn
   * @param message Use this param to override the default AND response message from server side
   */
  const notifyUserFromRequest = <TData, TError>(
    requestFn: () => Promise<AxiosResponse<TData, TError>>,
    message?: LoadingMessagesProps
  ) => {
    if (message?.loadingMessage) {
      setLoadingIndicators((curIndicator) => [
        ...curIndicator,
        message.loadingMessage!
      ]);
    }

    return requestFn()
      .then((result) => {
        if (message?.successMessage) {
          notifyUser({ intent: 'success', title: message.successMessage });
        }
        return result.data;
      })
      .catch((error) => {
        if (error && error instanceof AxiosError) {
          if (error.response) {
            const serverResponse = error.response as AxiosResponse<{
              error: string;
            }>;
            if (serverResponse.status) {
              const {
                isServerError,
                isMaxFileSizeUploadExceeded: isMaxFileUploadExceeded,
                isClientError,
                isNotFoundError
              } = evaluateHTTPCode(serverResponse.status as HTTP_STATUS);

              let isErrorHandled = false;
              if (isDefaultResponseData(error.response.data)) {
                if (
                  message?.errorIdHandler &&
                  error.response.data.type in message.errorIdHandler
                ) {
                  message.errorIdHandler[error.response.data.type](
                    error.response.data?.reasons
                  );
                  isErrorHandled = true;
                }
              }
              if (!isErrorHandled) {
                if (
                  isClientError &&
                  'msg' in serverResponse.data &&
                  typeof serverResponse.data?.msg === 'string'
                ) {
                  notifyUser({
                    intent: 'error',
                    title: serverResponse.data.msg
                  });
                } else if (isNotFoundError) {
                  // do nothing
                } else if (serverResponse.status === 403) {
                  notifyUser({
                    intent: 'error',
                    title: t('globalNotifications.forbidden')
                  });
                } else if (isMaxFileUploadExceeded) {
                  notifyUser({
                    intent: 'error',
                    title: t('globalNotifications.maxFileSizeUploadExceeded')
                  });
                } else if (isServerError) {
                  notifyUser({
                    intent: 'error',
                    title:
                      message?.errorMessage ??
                      t('globalNotifications.alertServerError')
                  });
                } else {
                  notifyUser({
                    intent: 'error',
                    title: message?.errorMessage ?? serverResponse?.data?.error
                  });
                }
              }
            }
          } else {
            notifyUser({
              intent: 'error',
              title: t('api.error.serviceNotAvailable')
            });
          }
        } else if (message && message.successMessage) {
          notifyUser({ intent: 'success', title: message.successMessage });
        }
        throw new Error(error.message, { cause: error.response });
      })
      .finally(() => {
        if (message?.loadingMessage) {
          setLoadingIndicators((curIndicators) =>
            curIndicators.filter(
              (curIndicator) => curIndicator !== message.loadingMessage
            )
          );
        }
      });
  };

  return { confirmByUser, notifyUserFromRequest, notifyUser };
}
